// Fluxo OAuth2 Authorization Code (+PKCE +state) para o SSO do SOP & RAFT,
// com captura robusta do retorno (query OU hash; code / token / access_token).
import { setToken, getToken, exchangeSso } from "@/lib/atlasApi";

const SS_STATE = "sop_oauth_state";
const SS_VERIFIER = "sop_oauth_verifier";

function b64url(bytes) {
  let s = "";
  bytes.forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function randomValue(n = 32) {
  const data = new Uint8Array(n);
  crypto.getRandomValues(data);
  return b64url(data);
}
async function challenge(verifier) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}
function redirectUriOf(config) {
  return config?.sso_redirect_uri || (typeof window !== "undefined" ? window.location.origin : "");
}

function cleanUrl() {
  try {
    const url = new URL(window.location.href);
    ["code", "token", "access_token", "id_token", "state", "token_type", "expires_in", "session", "scope"].forEach(
      (k) => url.searchParams.delete(k)
    );
    window.history.replaceState(null, "", `${url.pathname}${url.search}`);
  } catch (_) { /* noop */ }
}

/** Inicia o login: OBRIGATORIAMENTE response_type=code + PKCE S256 + state. */
export async function beginSsoLogin(config) {
  const base = config?.sso_login_url;
  if (!base || !config?.sso_client_id) return;
  const params = new URLSearchParams();
  params.set("response_type", "code");
  params.set("client_id", config.sso_client_id);
  params.set("redirect_uri", redirectUriOf(config));
  params.set("scope", config?.sso_scope || "openid profile email");

  const state = randomValue();
  sessionStorage.setItem(SS_STATE, state);
  params.set("state", state);

  const verifier = randomValue(32);
  sessionStorage.setItem(SS_VERIFIER, verifier);
  params.set("code_challenge", await challenge(verifier));
  params.set("code_challenge_method", "S256");

  window.location.assign(`${base}?${params.toString()}`);
}

/**
 * Captura e conclui a autenticação a partir da URL de retorno.
 * Lê tanto a query (?...) quanto o hash (#...) e aceita:
 *   - access_token / token / id_token  -> JWT direto (armazena)
 *   - code                             -> troca por token no backend
 * Retorna: "ok" | "none" | "error".
 */
export async function captureAndCompleteAuth(config) {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
  const pick = (k) => search.get(k) || hash.get(k);

  const directToken = pick("access_token") || pick("token") || pick("id_token");
  const code = pick("code");
  if (!directToken && !code) return "none";

  try {
    if (code) {
      const returnedState = pick("state");
      const expected = sessionStorage.getItem(SS_STATE);
      // Valida o state apenas se nós mesmos geramos um (evita falso negativo).
      if (expected && returnedState && returnedState !== expected) {
        throw new Error("state_mismatch");
      }
      const verifier = sessionStorage.getItem(SS_VERIFIER) || undefined;
      console.info("[SSO] Trocando authorization code por token…", {
        redirect_uri: redirectUriOf(config),
        has_code_verifier: Boolean(verifier),
      });
      const data = await exchangeSso({
        code,
        redirect_uri: redirectUriOf(config),
        code_verifier: verifier,
        state: returnedState || undefined,
      });
      if (data?.access_token) {
        setToken(data.access_token);
        console.info("[SSO] Token recebido com sucesso.");
      }
    } else if (directToken) {
      setToken(directToken);
      console.info("[SSO] Token direto capturado da URL de retorno.");
    }
    return { status: getToken() ? "ok" : "error" };
  } catch (e) {
    const resp = e?.response;
    const detail = resp?.data?.detail || resp?.data?.code || e?.message || "erro desconhecido";
    console.error("[SSO] Falha na troca do código / autenticação:", {
      httpStatus: resp?.status,
      body: resp?.data,
      message: e?.message,
    });
    return { status: "error", detail };
  } finally {
    sessionStorage.removeItem(SS_STATE);
    sessionStorage.removeItem(SS_VERIFIER);
    cleanUrl();
  }
}
